import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Download } from 'lucide-react';
import { listInvoices } from '@mkcore/portal/schema';
import { Badge, Card, EmptyState, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';

const search = z.object({ state: z.enum(['open', 'all']).optional() });

/** The invoices of the client (CO-0661): what went to it, what is open, and the documents. */
export const Route = createFileRoute('/portal/_client/invoices/')({
  validateSearch: search,
  component: Invoices,
});

const tone = { open: 'info', overdue: 'danger', paid: 'success', credited: 'neutral', credit: 'neutral' } as const;

function Invoices() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['portal', 'invoices'], queryFn: () => call(listInvoices, { state: 'all' }) });
  if (!q.data) return <Skeleton className="h-40" />;
  const { items, openCents, overdueCents } = q.data;
  const currency = items[0]?.currency ?? 'EUR';
  return (
    <>
      <h1 className="text-2xl font-bold">{t('portal.modules.invoices')}</h1>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="p-4">
          <p className="text-sm text-fg-2">{t('portal.invoices.open')}</p>
          <p className="text-2xl font-bold tabular">{formatMoney(openCents, currency)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-fg-2">{t('portal.invoices.overdue')}</p>
          <p className={`text-2xl font-bold tabular ${overdueCents ? 'text-danger' : ''}`}>{formatMoney(overdueCents, currency)}</p>
        </Card>
      </div>
      {!items.length ? (
        <EmptyState title={t('portal.invoices.empty')} />
      ) : (
        <ul className="grid gap-2" aria-label={t('portal.modules.invoices')}>
          {items.map((i) => (
            <li key={i.id}>
              <Card className="grid gap-2 p-4 sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="grid gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold tabular">{i.number}</span>
                    <Badge tone={tone[i.state]}>{t(`portal.invoices.state.${i.state}`)}</Badge>
                  </div>
                  <p className="text-xs text-fg-2">
                    {[i.issueDate && t('portal.invoices.issued', { date: formatDate(i.issueDate) }), i.dueDate && i.openCents > 0 && t('portal.invoices.due', { date: formatDate(i.dueDate) }), i.reference, i.credits && t('portal.invoices.credits', { number: i.credits })]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="grid gap-1 sm:text-right">
                  <span className="font-bold tabular">{formatMoney(i.totalCents, i.currency)}</span>
                  {i.openCents > 0 && i.openCents !== i.totalCents && <span className="text-xs text-fg-2 tabular">{t('portal.invoices.stillOpen', { amount: formatMoney(i.openCents, i.currency) })}</span>}
                  <span className="flex flex-wrap gap-3 text-sm sm:justify-end">
                    <a href={`/api/v1/portal/invoices/${i.id}/pdf?download=1`} className="flex items-center gap-1 underline" aria-label={t('portal.invoices.pdfOf', { number: i.number })}>
                      <Download className="size-4" aria-hidden /> PDF
                    </a>
                    {i.hasSpecification && (
                      <a href={`/api/v1/portal/invoices/${i.id}/specification?download=1`} className="underline" aria-label={t('portal.invoices.specOf', { number: i.number })}>
                        {t('portal.invoices.specification')}
                      </a>
                    )}
                    {i.hasUbl && (
                      <a href={`/api/v1/portal/invoices/${i.id}/ubl?download=1`} className="underline" aria-label={t('portal.invoices.ublOf', { number: i.number })}>
                        UBL
                      </a>
                    )}
                  </span>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
